
import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { hasRole } from '@/utils/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [isCheckingRole, setIsCheckingRole] = useState(requiredRole !== undefined);
  const [hasRequiredRole, setHasRequiredRole] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  console.log('ProtectedRoute - Initial State:', { 
    user, 
    isAuthenticated, 
    isLoading, 
    requiredRole, 
    isCheckingRole 
  });
  
  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (isLoading || isCheckingRole) {
        console.log('ProtectedRoute - Loading timeout reached');
        setLoadingTimeout(true);
      }
    }, 3000); // 3 second timeout
    
    return () => clearTimeout(timeout);
  }, [isLoading, isCheckingRole]);
  
  useEffect(() => {
    console.log('ProtectedRoute - Effect triggered:', { 
      user, 
      isAuthenticated, 
      isLoading, 
      requiredRole, 
      isCheckingRole 
    });
    
    // Only check role if we're requiring one and user is authenticated
    if (requiredRole && user) {
      console.log('Checking if user has role:', requiredRole);
      const userHasRole = hasRole(user, requiredRole);
      console.log('Role check result:', userHasRole, 'User roles:', user.roles);
      setHasRequiredRole(userHasRole);
      setIsCheckingRole(false);
    } else if (!user) {
      console.log('No user available, skipping role check');
      setIsCheckingRole(false);
    }
  }, [user, requiredRole]);

  // If loading timeout is reached but still loading, show error message
  if (loadingTimeout) {
    console.log('ProtectedRoute - Loading timeout triggered, proceeding with available data');
    
    // If not authenticated, redirect to home
    if (!user) {
      console.log('ProtectedRoute - User not authenticated after timeout, redirecting to home');
      return <Navigate to="/" replace />;
    }
    
    // If role is required, check if we can determine role status
    if (requiredRole) {
      const userHasRole = hasRole(user, requiredRole);
      if (!userHasRole) {
        console.log('ProtectedRoute - User does not have required role after timeout:', requiredRole);
        return <Navigate to="/" replace />;
      }
    }
    
    // If we get here, let the user proceed
    console.log('ProtectedRoute - After timeout: proceeding with available data');
    return <>{children}</>;
  }

  // Show loading state if auth is still initializing or if role check is pending
  if (isLoading || isCheckingRole) {
    console.log('ProtectedRoute - Showing loading state:', { isLoading, isCheckingRole });
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated) {
    console.log('ProtectedRoute - User not authenticated, redirecting to home');
    return <Navigate to="/" replace />;
  }

  // If role check is required and user doesn't have role
  if (requiredRole && !hasRequiredRole) {
    console.log('ProtectedRoute - User does not have required role:', requiredRole);
    return <Navigate to="/" replace />;
  }

  // User is authenticated and has required role (if specified)
  console.log('ProtectedRoute - Access granted, rendering children');
  return <>{children}</>;
}
